
import { useState } from "react";
import { Download, Loader2, FolderDown, CheckCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

interface TransferInfo {
  filename: string;
  original_name: string;
  size: number;
  expires_at: string;
  content_type: string;
}

interface DownloadContentProps {
  loading: boolean;
  error: string | null;
  transfer: TransferInfo | null;
  downloading: boolean;
  onDownload: () => Promise<void>;
}

export function DownloadContent({
  loading,
  error,
  transfer,
  downloading,
  onDownload,
}: DownloadContentProps) {
  const navigate = useNavigate();
  const [downloaded, setDownloaded] = useState(false);
  const isMobile = useIsMobile();

  const handleDownload = async () => {
    await onDownload();
    setDownloaded(true);
  };

  // Verificación de datos válidos
  const isValidTransfer = transfer && 
    transfer.original_name && 
    typeof transfer.size === 'number' &&
    transfer.expires_at;

  console.log("Transfer data validation:", {
    transfer,
    isValidTransfer,
    hasOriginalName: transfer?.original_name,
    hasValidSize: typeof transfer?.size === 'number',
    hasExpiryDate: transfer?.expires_at
  });

  return (
    <div className="relative z-10 h-[calc(100%-9rem)] flex items-center">
      <div className="w-full max-w-md mx-4 sm:mx-0 sm:ml-8">
        <div className="bg-black/40 backdrop-blur-xl rounded-xl p-4 sm:p-6 shadow-lg border border-white/10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12">
              <Loader2 className="h-6 sm:h-8 w-6 sm:w-8 animate-spin text-white" />
              <p className="mt-4 text-sm sm:text-base text-white">Cargando información del archivo...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-sm sm:text-base text-red-500 font-medium">{error}</p>
            </div>
          ) : isValidTransfer ? (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                {downloaded ? (
                  <>
                    <CheckCircle 
                      className={`${isMobile ? 'h-16 w-16' : 'h-24 w-24'} text-[#00F5D0]`}
                      strokeWidth={1.5}
                    />
                    <h1 className="text-xl sm:text-2xl font-semibold text-white text-center">
                      Tu descarga ha comenzado
                    </h1>
                  </>
                ) : (
                  <>
                    <FolderDown 
                      className={`${isMobile ? 'h-12 w-12' : 'h-16 w-16'} text-white`}
                      strokeWidth={1.5}
                    />
                    <h1 className="text-xl sm:text-2xl font-semibold text-white text-center">
                      Archivo listo para descargar
                    </h1>
                    <p className="text-sm sm:text-base text-white/80 text-center">
                      El archivo estará disponible hasta{" "}
                      {formatDistanceToNow(new Date(transfer.expires_at), {
                        addSuffix: true,
                      })}
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-4">
                {!downloaded && transfer.original_name && (
                  <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/10">
                    <p className="font-medium text-white text-sm sm:text-base break-all">
                      {transfer.original_name}
                    </p>
                    <p className="text-xs sm:text-sm text-white/80 mt-1">
                      {(transfer.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}

                {downloaded ? (
                  <button
                    className="w-full py-2 px-4 rounded-lg hover:opacity-90 transition-colors duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base"
                    onClick={() => navigate('/')}
                    style={{
                      backgroundColor: '#00F5D0',
                      color: '#0A0D14',
                    }}
                  >
                    <span>¿Mandar un nuevo archivo?</span>
                  </button>
                ) : (
                  <button
                    className="w-full py-2 px-4 rounded-lg hover:opacity-90 transition-colors duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base"
                    onClick={handleDownload}
                    disabled={downloading}
                    style={{
                      backgroundColor: '#00F5D0',
                      color: '#0A0D14',
                    }}
                  >
                    {downloading ? (
                      <>
                        <Loader2 className="h-5 w-5 sm:h-7 sm:w-7 animate-spin" />
                        <span>Iniciando descarga...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 sm:h-7 sm:w-7" />
                        <span>Descargar archivo</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <p className="text-sm sm:text-base text-red-500 font-medium">
                No se pudo cargar la información del archivo correctamente
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
